import Analytics from '@opencare/analytics';
import { get } from 'lodash';

import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Form } from 'opencare-crown';

import {
  DIMENSIONS,
  useWindowDimensions,
} from 'providers/WindowDimensionsProvider';
import { useAnalyticsService } from 'services/AnalyticsService';
import sanitizeString from 'utils/sanitizeString';
import scrollToRef from 'utils/scrollToRef';

import FloatingInput from '../../../../common/components/FloatingInput/FloatingInput';
import MultiSelect from '../../../../common/components/MultiSelect/MultiSelect';
import {
  AdditionalBlock,
  OptionBlock,
  PrimaryButton,
  Subtitle,
  Title,
} from '../../../../common/styled-components';
import { useFunnelStorage } from '../../../storage/Storage';
import { ButtonBlock, StepWrapper } from '../../../styled-components';
import { generateStepAnalytics, STEP_KEY } from '../../../utils';
import { treatmentCheckupCleaning, treatments } from '../constants';

const analytics = new Analytics();

const Treatment = ({ position, moveToNextStep }) => {
  const storage = useFunnelStorage();
  const refToAdditionalDetails = useRef();
  const { layout } = useWindowDimensions();

  const analyticsService = useAnalyticsService();

  const [treatmentForm] = Form.useForm();

  const savedReason = storage.get(STEP_KEY.REASON_DETAILS);

  // Append Check-up & Cleaning as a first option.
  // https://app.asana.com/0/1167683449390038/1206052424254718
  const treatmentOptions = [treatmentCheckupCleaning, ...treatments];

  const [selectedTreatments, setSelectedTreatments] = useState(
    get(savedReason, 'treatments') ? savedReason.treatments : [],
  );
  const [otherReason, setOtherReason] = useState(
    get(savedReason, 'other') ? savedReason.other : '',
  );

  const otherIsSelected = selectedTreatments.find(
    (treatment) => treatment.title === 'Other',
  );

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_services',
      ...generateStepAnalytics(position, storage),
      ...analyticsService.analyticsData,
    });
  }, []);

  useEffect(() => {
    if (otherIsSelected) {
      scrollToRef(refToAdditionalDetails);
    }
  }, [otherIsSelected]);

  const selectTreatment = (choice) => {
    const previouslySelected = selectedTreatments.find(
      (treatment) => treatment.id === choice.id,
    );

    if (previouslySelected) {
      const newTreatments = selectedTreatments.filter(
        (treatment) => treatment.id !== choice.id,
      );

      setSelectedTreatments(newTreatments);
    } else {
      setSelectedTreatments([...selectedTreatments, choice]);
    }
  };

  const complete = () => {
    const selectedTreatmentsAlt = selectedTreatments.filter(
      (treatment) => treatment.id !== treatmentCheckupCleaning.id,
    );

    analytics.trackClick({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_services',
      name: 'wizard',
      section: 'reason-dentist',
      position: selectedTreatmentsAlt.map((treatment) =>
        treatmentOptions.findIndex((option) => option.id === treatment.id)),
      ...generateStepAnalytics(position, storage, {
        response:
          selectedTreatmentsAlt
            .map((treatment) => treatment.data || treatment.title)
            .join(',')
            + (otherIsSelected
              ? ` - othernote: ${sanitizeString(otherReason)}`
              : ''),
      }),
    });

    const previouslySaved = storage.get(STEP_KEY.REASON_DETAILS);
    const savedData = {
      ...previouslySaved,
      reason: 'Checkup & Cleaning',
      reasonId: 'checkup',
      treatments: selectedTreatmentsAlt.map((treatment) => ({
        title: treatment.data || treatment.title,
        id: treatment.id,
      })),
      other: otherIsSelected ? sanitizeString(otherReason) : null,
      urgentReason: null,
    };

    storage.save(STEP_KEY.REASON_DETAILS, savedData, true);

    moveToNextStep();
  };

  return (
    <div data-testid="substep-treatment" id="substep-treatment">
      {layout === DIMENSIONS.MOBILE
        ? (
          <Title>
            Do you have any specific concerns or treatments you are seeking?
          </Title>
        )

        : (
          <>
            <Title>Do you have any specific concerns</Title>
            <Title>or treatments you are seeking?</Title>
          </>
        )}
      <Subtitle>Please select any treatments you require</Subtitle>
      <StepWrapper>
        <Form form={treatmentForm} onFinish={complete}>
          <OptionBlock row={4}>
            {treatmentOptions.map((treatment, i) => (
              <MultiSelect
                key={treatment.id}
                name="treatments"
                id={treatment.id}
                title={`${treatment.title}${
                  treatment.title === 'Other' ? '...' : ''
                }`}
                tooltip={treatment.description}
                onChange={() => selectTreatment(treatment, i)}
                initialSelected={selectedTreatments.findIndex(
                  (saved) => saved.id === treatment.id,
                ) > -1}
              />
            ))}
          </OptionBlock>
          {otherIsSelected
          && (
            <AdditionalBlock ref={refToAdditionalDetails} tabIndex="-1">
              <FloatingInput
                field="otherReason"
                form={treatmentForm}
                label="Describe other treatments"
                initialValue={otherReason}
                testId="other-additional"
                capitalize={false}
                onChange={(e) => setOtherReason(e.target.value)}
              />
            </AdditionalBlock>
          )}
          <ButtonBlock>
            <PrimaryButton
              type="submit"
              disabled={otherIsSelected && !otherReason.trim()}
              data-testid="continue"
            >
              {selectedTreatments.length === 0
                ? 'I just need a check-up and cleaning'
                : 'Continue'}
            </PrimaryButton>
          </ButtonBlock>
        </Form>
      </StepWrapper>
    </div>
  );
};

Treatment.defaultProps = {
  position: '',
};

Treatment.propTypes = {
  position: PropTypes.string,
  moveToNextStep: PropTypes.func.isRequired,
};

export default Treatment;
